import { cardAppearance } from '@src/components/PersonCard/cardAppearance';
import { ThemeArtwork } from '@src/theme/Artwork';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { participantCardStatus } from './participantCardStatus';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Columns, ParticipantFace, ParticipantTile, Stack, Typography } from '@src/components';

import { boxExchangePath, boxOrganizerPath } from '@src/routing/routes';
import './ParticipantsGrid.css';

export interface ParticipantsGridProps {
  info: BoxInfo;
  cards: CardInfo[];
}

function participantOrder(card: CardInfo, activeDraw: boolean): number {
  if (card.isYou) return 0;
  if (!card.isVerified) return 4;
  if (card.isGiftOnItsWay || card.isGiftReceived) return 3;
  if (activeDraw && card.isWardKnown) return 1;
  return 2;
}

export function ParticipantsGrid({ info, cards }: ParticipantsGridProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const participants = [...cards]
    .sort((a, b) => participantOrder(a, Boolean(info.isActiveDraw)) - participantOrder(b, Boolean(info.isActiveDraw)));

  return (
    <Stack gap={5} testId="game-people">
      {participants.length === 0 && (
        <Typography as="p" role="body/base">
          {t('game.people.noParticipants')}
        </Typography>
      )}
      <Columns columns={3} gap={4} className="participants-grid">
        {participants.map((card, index) => {
          const isExchangeCard = card.isYou || card.isYourWard;
          const href = isExchangeCard
            ? boxExchangePath(info.box.key)
            : info.isAdmin ? boxOrganizerPath(info.box.key) : undefined;
          const label =
            (info.box.useNames || card.isYou || card.isYourWard) && card.username !== 'unknown'
              ? card.username
              : t('game.people.nameHidden');
          const status = participantCardStatus(card, info);
          const meta = t(`game.people.status.${status}`);

          return (
            <ParticipantTile
              listLayout
              key={card.id}
              {...cardAppearance(card.id)}
              // The stable ID keeps the theme variant independent of participant sorting.
              className={`participant-tile--mobile-row participant-tile--variant-${Math.abs(Math.trunc(card.id)) % 6 + 1}`}
              avatar={<ParticipantFace card={card} index={index} size="identity" />}
              name={label}
              meta={meta}
              metaIcon={
                status === 'giftReceived' ? (
                  <ThemeArtwork role="gift" className="participants-grid__received-gift" />
                ) : status === 'giftOnWay' ? (
                  <GiftIcon className="participant-tile__meta-icon" />
                ) : (
                  false
                )
              }
              muted={!card.isVerified}
              confirmed={Boolean(card.isVerified)}
              onClick={href ? () => navigate(href, isExchangeCard ? undefined : { state: { previewCardId: card.id } }) : undefined}
              disabled={!href}
              testId={`game-card-${card.id}`}
            />
          );
        })}
      </Columns>
    </Stack>
  );
}
